import { BrowserRouter as Router, Routes, Route, useLocation, useSearchParams } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { trackPageView } from './lib/analytics';
import Layout from './components/Layout';

// Route-level code splitting: all page components are lazy-loaded so the main
// bundle stays lean for every entry point (service pages, quote funnel, etc.)
const LandingPage = lazy(() => import('./components/LandingPage'));
const ForPartnersPage = lazy(() => import('./pages/ForPartnersPage').then(m => ({ default: m.ForPartnersPage })));
const ForCustomersPage = lazy(() => import('./pages/ForCustomersPage').then(m => ({ default: m.ForCustomersPage })));
const SeedPage = lazy(() => import('./pages/SeedPage').then(m => ({ default: m.SeedPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const DueDiligencePage = lazy(() => import('./pages/DueDiligencePage').then(m => ({ default: m.DueDiligencePage })));
const FullQuotePage = lazy(() => import('./pages/FullQuotePage').then(m => ({ default: m.FullQuotePage })));
const ReturnToQuotePage = lazy(() => import('./pages/ReturnToQuotePage').then(m => ({ default: m.ReturnToQuotePage })));
const QuoteStep1 = lazy(() => import('./pages/quote/Step1').then(m => ({ default: m.Step1 })));
const QuoteStep2 = lazy(() => import('./pages/quote/Step2').then(m => ({ default: m.Step2 })));
const QuoteStep3 = lazy(() => import('./pages/quote/Step3').then(m => ({ default: m.Step3 })));
const QuoteViewQuote = lazy(() => import('./pages/quote/ViewQuote').then(m => ({ default: m.ViewQuote })));
const QuoteViewQuote1 = lazy(() => import('./pages/quote/ViewQuote1').then(m => ({ default: m.ViewQuote1 })));
const QuoteSuccess = lazy(() => import('./pages/quote/Success').then(m => ({ default: m.Success })));
const QuoteNoEngineer = lazy(() => import('./pages/quote/NoEngineer').then(m => ({ default: m.NoEngineer })));
const QuoteFunnelPage = lazy(() => import('./pages/quote/QuoteFunnel').then(m => ({ default: m.QuoteFunnel })));
const DynamicServiceRoute = lazy(() => import('./components/DynamicServiceRoute'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

import { useQuoteStore } from './store/useQuoteStore';

function PageViewTracker() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setTrackingParams, gclid } = useQuoteStore();
  
  useEffect(() => {
    trackPageView(location.pathname + location.search);
    
    // Capture tracking params globally on any page load if present
    const urlGclid = searchParams.get('gclid');
    const urlMsclkid = searchParams.get('msclkid');
    const urlFbclid = searchParams.get('fbclid');
    const urlCustomerRef = searchParams.get('customerRef');
    
    if (urlGclid || urlMsclkid || urlFbclid || urlCustomerRef) {
      setTrackingParams({
        gclid: urlGclid || gclid,
        msclkid: urlMsclkid || 'unavailable',
        fbclid: urlFbclid || 'unavailable',
        customerRef: urlCustomerRef || '',
      });
    }
  }, [location, searchParams, setTrackingParams]);
  
  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'auto' });
        }
      }, 50);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

export default function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <PageViewTracker />
        <Layout>
          <Suspense fallback={<div className="w-full min-h-screen" aria-hidden="true" />}>
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-policy-4" element={<PrivacyPolicyPage />} />
            <Route path="/terms-conditions" element={<TermsPage />} />
            <Route path="/contact-us" element={<ContactPage />} />
            <Route path="/due-diligence-checks" element={<DueDiligencePage />} />
            <Route path="/seed" element={<SeedPage />} />
            <Route path="/quote" element={<FullQuotePage />} />
            <Route path="/quote-form-test" element={<QuoteFunnelPage />} />
            <Route path="/viewQuote" element={<ReturnToQuotePage />} />
            <Route path="/step1Page" element={<QuoteStep1 />} />
            <Route path="/step2" element={<QuoteStep2 />} />
            <Route path="/step3" element={<QuoteStep3 />} />
            <Route path="/view-quote" element={<QuoteViewQuote />} />
            <Route path="/viewQuote1" element={<QuoteViewQuote1 />} />
            <Route path="/success" element={<QuoteSuccess />} />
            <Route path="/no-engineer" element={<QuoteNoEngineer />} />

            <Route path="/partners" element={<ForPartnersPage />} />
            <Route path="/customers" element={<ForCustomersPage />} />
            {/* Dynamic slug route handles all services and -in-location variations */}
            <Route path="/:serviceSlug" element={<DynamicServiceRoute />} />
            <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Layout>
      </Router>
    </HelmetProvider>
  );
}
